/**
 * Billing Selectors
 * Selectors for accessing billing state
 */

import { createSelector } from '@reduxjs/toolkit';
import { BillingState, PlanRateLimit } from '../../types/billing';

interface RootState {
  billing: BillingState;
}

const selectBillingState = (state: RootState): BillingState => state.billing;

export const selectSubscription = createSelector(
  [selectBillingState],
  (billing) => billing.subscription,
);

export const selectSubscriptionPackages = createSelector(
  [selectBillingState],
  (billing) => billing.packages,
);

export const selectInvoices = createSelector(
  [selectBillingState],
  (billing) => billing.invoices,
);

export const selectUsage = createSelector(
  [selectBillingState],
  (billing) => billing.usage,
);

export const selectBillingLoading = createSelector(
  [selectBillingState],
  (billing) => billing.isLoading,
);

export const selectBillingError = createSelector(
  [selectBillingState],
  (billing) => billing.error,
);

export const selectPlanRateLimits = createSelector(
  [selectBillingState],
  (billing): Record<string, PlanRateLimit> => billing.planRateLimits,
);

export const selectCurrentPackage = createSelector(
  [selectSubscription, selectSubscriptionPackages],
  (subscription, packages) =>
    subscription
      ? packages.find((p) => p.id === subscription.package) || null
      : null,
);

export const selectIsTrialing = createSelector(
  [selectSubscription],
  (subscription) => subscription?.subscription_status === 'trialing',
);

export const selectIsCanceled = createSelector(
  [selectSubscription],
  (subscription) =>
    subscription?.subscription_status === 'canceled' ||
    subscription?.cancel_at_period_end === true,
);

export const selectTrialDaysRemaining = createSelector(
  [selectSubscription, selectInvoices],
  (subscription, invoices) => {
    if (!subscription) return 0;

    const { subscription_status } = subscription;

    // past_due: count down to grace period expiry (the last chance to pay)
    if (subscription_status === 'past_due') {
      const graceEnd = subscription.grace_period_ends_at;
      if (!graceEnd) return 0;
      const diffDays = Math.ceil(
        (new Date(graceEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
      return Math.max(0, diffDays);
    }

    // trialing: count down to trial expiry
    if (subscription_status === 'trialing') {
      const trialEnd = subscription.trial_ends_at;
      if (!trialEnd) return 0;
      const diffDays = Math.ceil(
        (new Date(trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
      return Math.max(0, diffDays);
    }

    // active / all other statuses: use current_period_end, fall back to the
    // most recent paid invoice's period_end when Stripe hasn't synced yet
    let countdownDate: string | null = subscription.current_period_end;

    if (!countdownDate) {
      const latestPaid = [...invoices]
        .filter((i) => i.status === 'paid' && i.period_end)
        .sort(
          (a, b) =>
            new Date(b.paid_at || b.invoice_date).getTime() -
            new Date(a.paid_at || a.invoice_date).getTime(),
        )[0];
      countdownDate = latestPaid?.period_end ?? null;
    }

    if (!countdownDate) return 0;
    const diffDays = Math.ceil(
      (new Date(countdownDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
    );
    return Math.max(0, diffDays);
  },
);

export const selectBillingTimeLeftLabel = createSelector(
  [selectTrialDaysRemaining],
  (daysLeft) => {
    if (daysLeft < 30) {
      return `${daysLeft.toLocaleString()} DAYS LEFT`;
    }

    const monthsLeft = Math.max(1, Math.ceil(daysLeft / 30));
    return `${monthsLeft.toLocaleString()} MONTHS LEFT`;
  },
);

// Grace period: past_due AND grace window has not yet expired
export const selectIsSubscriptionInGracePeriod = createSelector(
  [selectSubscription],
  (subscription): boolean => {
    if (subscription?.subscription_status !== 'past_due') return false;
    const graceEnd = subscription.grace_period_ends_at;
    if (!graceEnd) return false;
    return new Date(graceEnd).getTime() > Date.now();
  },
);

export const selectGracePeriodEndsAt = createSelector(
  [selectSubscription],
  (subscription): string | null =>
    subscription?.subscription_status === 'past_due'
      ? (subscription.grace_period_ends_at ?? null)
      : null,
);

export const selectUpcomingInvoice = createSelector(
  [selectInvoices],
  (invoices) => {
    const upcoming = invoices.filter(
      (i) => i.status === 'draft' || i.status === 'open',
    );
    return (
      upcoming.sort(
        (a, b) =>
          new Date(a.due_date || a.invoice_date).getTime() -
          new Date(b.due_date || b.invoice_date).getTime(),
      )[0] || null
    );
  },
);

export const selectPaidInvoices = createSelector([selectInvoices], (invoices) =>
  invoices
    .filter((i) => i.status === 'paid')
    .sort(
      (a, b) =>
        new Date(b.paid_at || b.invoice_date).getTime() -
        new Date(a.paid_at || a.invoice_date).getTime(),
    ),
);
